import { useState } from 'react';

import { IS_APP_BUILD } from '@/lib/appBrand';

/**
 * Bil mascot with random variety. Each "pose" can have several image variants
 * (bil_<pose>.png, bil_<pose>_2.png, bil_<pose>_3.png …). One is picked at
 * random on every mount, so Bil feels alive and doesn't show the exact same
 * picture on every visit. Missing variants fall back to the base image.
 *
 * When new variant files are added for a pose, bump its count here.
 */
// Only the base canonical image per pose is used right now. The extra _2/_3
// variants were auto-generated for variety but some drifted off-model (wrong
// glasses / snout / proportions), so they're disabled until each is
// individually vetted against the canonical Bil. Base images are hand-verified.
// Each pose has 2-3 mood variants (bil_<pose>.png / _2 / _3), picked at random
// per mount so Bil feels alive. Sourced from the 3D render set (bg removed).
// Пригодные варианты каждой позы — списком, а не количеством.
//
// ПОЧЕМУ НЕ ЧИСЛОМ. Раньше здесь стояло «сколько вариантов есть», и один
// выбирался случайно. Часть картинок вырезана так, что фигура упирается в край
// кадра, и срез виден на экране: у `point_2` хвост обрывается вертикально
// посреди карточки, у `teacher` — то же самое, причём это БАЗОВЫЙ файл позы.
// Заказчик прислал снимок именно такой карточки 17.08.2026.
//
// Срез СНИЗУ безвреден: маскот прижат к нижнему краю и выглядит выглядывающим.
// Срез СПРАВА или СВЕРХУ обрывает фигуру в воздухе — такие файлы исключены.
//
// Проверено по всем 40 файлам: 13 целых, 24 срезаны снизу (годятся),
// 3 справа и 1 сверху (`point_2`, `start_3`, `teacher`, `thinking_2`).
// Если добавятся новые картинки — проверить границы прежде, чем вносить сюда.
const VARIANTS: Record<string, string[]> = {
  hello: ['', '_2'],
  point: [''], // _2 срезан справа
  teacher: ['_2', '_3'], // базовый срезан справа
  start: ['', '_2'], // _3 срезан справа
  celebrate: ['', '_2', '_3'],
  encourage: ['', '_2', '_3'],
  thinking: [''], // _2 срезан сверху и снизу
  books: ['', '_2'],
  homework: ['', '_2'],
  chat: ['', '_2'],
  reading: ['', '_2', '_3'],
  listening: ['', '_2'],
  writing: ['', '_2'],
  speaking: ['', '_2'],
};

// Bump when the mascot art changes — the filenames are stable, so a version
// query busts the browser cache so everyone gets the new Bil, not a stale image.
//
// The art ships as WebP (converted from the PNG originals on 2026-08-16): the same
// forty images went from 27 MB to 3 MB, which matters because this folder rides
// inside the store build that users download. Originals live in
// `frontend/assets-src/mascot-png/`, outside the bundle. WebP is safe for our
// floors — iOS 15+ and Android 24+ both decode it natively.
const ART_VERSION = '3';

function baseSrc(pose: string) {
  return `/mascot/bil_${pose}.webp?v=${ART_VERSION}`;
}

function pickSrc(pose: string): string {
  const variants = VARIANTS[pose];
  if (!variants || variants.length === 0) return baseSrc(pose);
  const suffix = variants[Math.floor(Math.random() * variants.length)];
  return `/mascot/bil_${pose}${suffix}.webp?v=${ART_VERSION}`;
}

type MascotProps = { pose: string; className?: string; alt?: string };

/**
 * ⚠️ В STORE-СБОРКЕ МАСКОТА НЕТ (D76, решение владельца 25.09.2026): в 61 и 102 бренд 1 BILIM ACADEMY целиком,
 * лиса — персонаж OneBilim. С ребрендинга 1.1.1 лис стоит на иконке и заставке, но в экраны приложения он
 * вернётся только отдельным решением владельца (+3 МБ, вёрстка главной, профиля и мока — docs/CHECK-2026-10-04-REBRAND.md §4г). Компонент ничего не рисует, а папка `mascot/` не попадает в dist-mobile (плагин в
 * vite.config.ts). Отступ под маскота в MobileKit снимается там же по `IS_APP_BUILD`. Обёрткой, а не ранним
 * return перед useState: иначе rules-of-hooks. Сайт — без изменений.
 */
export function Mascot(props: MascotProps) {
  return IS_APP_BUILD ? null : <MascotImage {...props} />;
}

function MascotImage({ pose, className, alt = '' }: MascotProps) {
  // Pick once per mount (per page open) — stable for this render lifetime.
  const [src, setSrc] = useState(() => pickSrc(pose));
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        if (src !== baseSrc(pose)) setSrc(baseSrc(pose));
      }}
    />
  );
}
